import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Instagram, Facebook, Info, CheckCircle2, Loader2 } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { site, animalTypes } from '../mock/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: '', email: '', phone: '', animalType: '', vet: '', message: '', consent: false, company: '' };

export default function Contact() {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email';
    if (!form.phone.trim()) e.phone = 'Please enter your phone number';
    if (!form.animalType) e.animalType = 'Please select an animal type';
    if (!form.message.trim()) e.message = 'Please tell Kate how she can help';
    if (!form.consent) e.consent = 'Please confirm you understand veterinary consent is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (form.company) return; // honeypot
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        animalType: form.animalType,
        vet: form.vet,
        message: form.message,
        consent: form.consent,
        company: form.company,
      });
      setDone(true);
      setForm(empty);
      toast({ title: 'Enquiry sent', description: 'Thank you – Kate will be in touch as soon as possible.' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object') setErrors(detail);
      toast({
        title: 'Something went wrong',
        description: 'Please try again, or call Kate directly on ' + site.phone + '.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="bg-cream pt-20 pb-16 text-center">
        <div className="max-w-2xl mx-auto px-5">
          <p className="eyebrow text-sage-500 mb-3">Contact</p>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-ink mb-4">Get in Touch</h1>
          <p className="text-ink/60 text-lg">To find out more and book an appointment, call or email Kate, or send an enquiry below.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1120px] mx-auto px-5 grid lg:grid-cols-[1fr_1.3fr] gap-14">
          {/* Contact details */}
          <div className="reveal">
            <h2 className="text-3xl font-serif font-bold text-ink mb-8">Contact Details</h2>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage-50 flex items-center justify-center shrink-0"><Phone size={20} className="text-sage-600" /></div>
                <div>
                  <p className="text-ink/50 text-sm">Call or text Kate</p>
                  <a href={site.phoneHref} className="text-lg font-semibold text-ink hover:text-sage-600 transition-colors">{site.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage-50 flex items-center justify-center shrink-0"><Mail size={20} className="text-sage-600" /></div>
                <div>
                  <p className="text-ink/50 text-sm">Email</p>
                  <a href={`mailto:${site.email}`} className="text-lg font-semibold text-ink hover:text-sage-600 transition-colors break-all">{site.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sage-50 flex items-center justify-center shrink-0"><MapPin size={20} className="text-sage-600" /></div>
                <div>
                  <p className="text-ink/50 text-sm">Area covered</p>
                  <p className="text-lg font-semibold text-ink">{site.area}</p>
                </div>
              </li>
            </ul>
            <div className="flex gap-3 mt-8">
              <a href={site.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-sage-50 hover:bg-sage-500 hover:text-white text-sage-600 flex items-center justify-center transition-all"><Instagram size={19} /></a>
              <a href={site.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-sage-50 hover:bg-sage-500 hover:text-white text-sage-600 flex items-center justify-center transition-all"><Facebook size={19} /></a>
            </div>
            <div className="mt-8 flex items-start gap-3 bg-cream rounded-xl p-5">
              <Info size={20} className="text-sage-500 shrink-0 mt-0.5" />
              <p className="text-ink/70 text-sm leading-relaxed">Please note: prior to an initial consultation, veterinary consent is required. Kate will advise on the process for registering as a KD Veterinary Physiotherapy client.</p>
            </div>
          </div>

          {/* Form */}
          <div className="reveal">
            <div className="bg-white border border-cream-dark rounded-3xl shadow-lg p-8 md:p-10">
              <h2 className="text-3xl font-serif font-bold text-ink mb-7">Send an Enquiry</h2>
              {done ? (
                <div className="text-center py-10">
                  <CheckCircle2 size={56} className="text-sage-500 mx-auto mb-5" />
                  <h3 className="text-2xl font-serif text-ink mb-2">Thank you!</h3>
                  <p className="text-ink/65 mb-6">Your enquiry has been sent. Kate will get back to you as soon as possible.</p>
                  <button onClick={() => setDone(false)} className="text-sage-600 font-semibold hover:underline">Send another enquiry</button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" noValidate>
                  {/* Honeypot */}
                  <input type="text" name="company" value={form.company} onChange={(e) => set('company', e.target.value)} className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />

                  <Field label="Your name" error={errors.name}>
                    <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Smith" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@email.com" />
                    </Field>
                    <Field label="Phone" error={errors.phone}>
                      <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="07xxx xxxxxx" />
                    </Field>
                  </div>
                  <Field label="Animal type" error={errors.animalType}>
                    <Select value={form.animalType} onValueChange={(v) => set('animalType', v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {animalTypes.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Your vet / practice (optional)">
                    <Input value={form.vet} onChange={(e) => set('vet', e.target.value)} placeholder="Practice name" />
                  </Field>
                  <Field label="How can Kate help?" error={errors.message}>
                    <Textarea rows={4} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Tell Kate a little about your animal and how she can help..." />
                  </Field>
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-1" />
                      <span className="text-sm text-ink/70 leading-relaxed">I understand that veterinary consent is required before an initial consultation, and I am happy to request this from my vet.</span>
                    </label>
                    {errors.consent && <p className="text-destructive text-xs mt-1.5">{errors.consent}</p>}
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-sage-500 hover:bg-sage-600 disabled:opacity-70 text-white font-semibold py-4 rounded-full transition-all hover:shadow-lg flex items-center justify-center gap-2">
                    {submitting ? (<><Loader2 size={18} className="animate-spin" /> Sending...</>) : 'Send Enquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-ink/80 mb-2 block font-medium">{label} {!label.includes('optional') && <span className="text-sage-500">*</span>}</Label>
      {children}
      {error && <p className="text-destructive text-xs mt-1.5">{error}</p>}
    </div>
  );
}
